import { useRef, useState } from 'react';
import {
  publishingChannels,
  type PublishChannel,
  type PublishingSetting,
  type PublishingWeek,
} from '../publishingApi';
import { dayLabel, field, channelLabel } from './publishingUi';
import { ModalActions } from '../../../components/PromptDialog';
import { PublishingDialog } from './PublishingDialog';
export function AddPublication({
  error,
  date,
  candidates,
  settings,
  busy,
  close,
  save,
}: {
  error: string;
  settings: PublishingSetting[];
  date: string;
  candidates: PublishingWeek['candidates'];
  busy: boolean;
  close: () => void;
  save: (input: {
    requestId: string;
    sourcePostId: string;
    channels: PublishChannel[];
    date: string;
    time?: string;
  }) => Promise<void>;
}) {
  const [source, setSource] = useState('');
  const [time, setTime] = useState('18:00');
  const [channels, setChannels] = useState<PublishChannel[]>([...publishingChannels]);
  const [useDefaults, setUseDefaults] = useState(true);
  const request = useRef({ id: crypto.randomUUID(), content: '' });
  const submit = () => {
    const input = { sourcePostId: source, channels, date, ...(useDefaults ? {} : { time }) };
    const content = JSON.stringify(input);
    if (request.current.content !== content) request.current = { id: crypto.randomUUID(), content };
    void save({ ...input, requestId: request.current.id });
  };
  return (
    <PublishingDialog title="Beitrag einplanen" close={close} submit={submit}>
      {error && (
        <p role="alert" className="mb-3 text-sm">
          {error}
        </p>
      )}
      <p className="mb-3 text-sm">
        {dayLabel(date)} · Eine neue Fassung je gewähltem Kanal. Das vorhandene Video und seine Freigabe
        werden übernommen.
      </p>
      <label className="block text-sm">
        Video aus dem Bestand
        <select
          className={field}
          required
          value={source}
          onChange={(e) => setSource(e.target.value)}
          disabled={busy}
        >
          <option value="">Bitte wählen</option>
          {candidates.map((p) => (
            <option value={p.id} key={p.id}>
              {p.asset.slug} · {p.title || p.asset.title} · {channelLabel(p.channel)}
            </option>
          ))}
        </select>
      </label>
      {!candidates.length && (
        <p className="mt-2 text-sm">
          Noch keine hochgeladenen Videos vorhanden. Zuerst im Bestand eine Fassung vorbereiten.
        </p>
      )}
      <label className="my-3 flex gap-2 text-sm">
        <input
          type="checkbox"
          disabled={busy}
          checked={useDefaults}
          onChange={(e) => setUseDefaults(e.target.checked)}
        />
        Standardzeiten je Kanal verwenden
      </label>
      {!useDefaults && (
        <label className="my-3 block text-sm">
          Uhrzeit (Berlin)
          <input
            className={field}
            disabled={busy}
            required
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
        </label>
      )}
      <fieldset>
        <legend className="text-sm">Plattformen</legend>
        {publishingChannels.map((c) => (
          <label key={c} className="flex min-h-[44px] items-center gap-2 text-sm">
            <input
              type="checkbox"
              disabled={busy}
              checked={channels.includes(c)}
              onChange={(e) => setChannels((v) => (e.target.checked ? [...v, c] : v.filter((x) => x !== c)))}
            />
            {channelLabel(c)}
            {useDefaults ? ` · ${settings.find((s) => s.id === c)?.defaultTime ?? '18:00'}` : ''}
          </label>
        ))}
      </fieldset>
      <ModalActions
        onCancel={close}
        confirmLabel="In die Woche einplanen"
        disabled={busy || !source || !channels.length}
      />
    </PublishingDialog>
  );
}
